import React, { useState, useEffect } from 'react';
import { UserQuestion } from '../../types/UserQuestion';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/ThemeContext';
import './UserQuestionForm.css';

interface UserQuestionFormProps {
  question: UserQuestion | null;
  onSubmit: (question: UserQuestion) => void;
  onCancel: () => void;
  disableEmailEdit?: boolean;
}

const UserQuestionForm: React.FC<UserQuestionFormProps> = ({ 
  question, 
  onSubmit, 
  onCancel,
  disableEmailEdit = false
}) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [questionText, setQuestionText] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (question) {
      setQuestionText(question.questionText);
      setUserEmail(question.userEmail);
    } else {
      setQuestionText('');
      // Set default email to current user's email if available
      setUserEmail(user?.email || '');
    }
  }, [question, user]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    if (!questionText.trim()) {
      setError('Question text is required');
      return;
    }
    
    if (!userEmail.trim()) {
      setError('User email is required');
      return;
    }
    
    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Clear any previous errors
    setError(null);
    
    // Submit the form
    onSubmit({
      ...(question ? { id: question.id } : {}),
      questionText: questionText.trim(),
      userEmail: userEmail.trim()
    });
  };

  return (
    <div className={`form-container theme-${theme}`} data-theme={theme}>
      <h2>{question ? 'Edit Question' : 'Add New Question'}</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="questionText">Question Text</label>
          <input
            type="text"
            id="questionText"
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Enter question text"
            autoFocus
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="userEmail">User Email</label>
          <input
            type="email"
            id="userEmail"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            placeholder="Enter user email"
            disabled={disableEmailEdit}
          />
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            className="secondary-button" 
            onClick={onCancel}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="primary-button"
          >
            {question ? 'Update' : 'Create'} Question
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserQuestionForm; 